import { IReactionDisposer, observable, reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';
import { AnalyticsProvider, Container, EqualHeightProvider, Grid, GridColumn, Spinner, TGridColumnWidth } from 'telia-front-react';
import ProductCardWrapper from '../search/ProductCardWrapper';
import ProductDetailsStore from '../stores/ProductDetailsStore';
import ProductPriceStore from '../stores/ProductPriceStore';
import SsoStore from '../stores/SsoStore';
import { FetchStatus } from '../util/FetchStatus';
import { Product } from './ProductInterfaces';

interface ProductsWrapperProps {
  productCodes: string[];
  productGroup: string;
  productCardWidth?: TGridColumnWidth[];
  productDetailsStore?: ProductDetailsStore;
  ssoStore?: SsoStore;
  productPriceStore?: ProductPriceStore;
  children?: React.ReactNode;
}

@observer
class ProductsWrapper extends Component<ProductsWrapperProps> {
  private disposer: IReactionDisposer;
  @observable accessor customerId: number;

  static defaultProps: ProductsWrapperProps = {
    productCodes: [],
    productGroup: null,
    productCardWidth: ['xs-12', 'sm-6', 'lg-4']
  };

  public async componentDidMount() {
    const { productDetailsStore, ssoStore, productCodes, productGroup } = this.props;
    this.customerId = ssoStore.customerId;
    this.disposer = reaction(
      () => this.customerId,
      () => this.fetchPricesIfNeeded(),
      { fireImmediately: productDetailsStore.status !== FetchStatus.UNINITIALIZED }
    );
    await productDetailsStore.loadProducts(productGroup, productCodes);
  }

  public componentDidUpdate(prevProps: Readonly<ProductsWrapperProps>) {
    if (prevProps.ssoStore.customerId !== this.props.ssoStore.customerId) {
      this.customerId = this.props.ssoStore.customerId;
    }
  }

  public componentWillUnmount() {
    if (this.disposer) {
      this.disposer();
    }
  }

  private fetchPricesIfNeeded = () => {
    const { productDetailsStore, ssoStore, productCodes, productPriceStore } = this.props;
    if (ssoStore.customerId !== productPriceStore.lastCustomerId) {
      productDetailsStore.productsToShow(productCodes).length &&
        productPriceStore.fetchPrices(
          productDetailsStore.productsToShow(productCodes).map((p: Product) => p.productCode),
          productDetailsStore.productsToShow(productCodes)
        );
    }
  };

  public render() {
    const { productDetailsStore, productCodes } = this.props;
    const products = productDetailsStore.productsToShow(productCodes);
    if (!products?.length) {
      return null;
    } else if (productDetailsStore.isFetching()) {
      return <Spinner />;
    }
    return (
      <AnalyticsProvider group="Products">
        <Container>
          <EqualHeightProvider groups={['product-card-colors', 'product-card-title']}>
            <Grid equalHeight={true}>{this.renderProductCard(products)}</Grid>
          </EqualHeightProvider>
        </Container>
      </AnalyticsProvider>
    );
  }

  private renderProductCard(products: Product[]): JSX.Element[] {
    const { productCardWidth } = this.props;
    return products.map((product) => {
      return (
        <GridColumn key={`eshop-${product.ean}-grid`} width={productCardWidth}>
          <ProductCardWrapper key={`product-card-${product.productCode}`} product={product} />
        </GridColumn>
      );
    });
  }
}

export default inject('productDetailsStore', 'ssoStore', 'productPriceStore')(ProductsWrapper);
